import React from "react"
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, Grid } from "@material-ui/core"
import { ProjectDashboardViewModel } from "../../api/project-dashboard"
import { ChevronDown } from "react-feather"
import { keys } from "lodash"
import { COLOR_BLACK, COLOR_GRAY } from "../../../constants"
import { useTranslations } from "./useTranslations"
import { Status } from "../../../types/common"

const useResourceParticipation = ({ projectDashboard }: ResourceParticipationProps) => {
  const translations = useTranslations()
  const { tasksBasedOnResources } = projectDashboard

  const tasksBasedOnResourcesData = tasksBasedOnResources.map((item) => {
    const { resourceName, groupedCompletedTasks, groupedInProgressTasks, groupedNotStartedTasks } = item

    const completedStatusAndSubStatus = keys(groupedCompletedTasks)
    const completedData = completedStatusAndSubStatus.map((status) => {
      const { numberOfTasks: value, percentageOfTasks, tasks } = groupedCompletedTasks[status]
      const label = status === Status.COMPLETED ? translations.completed : status
      const boxColor = value ? COLOR_BLACK : COLOR_GRAY
      const percentage = percentageOfTasks.toFixed(1)
      return { label, boxColor, value, percentage, tasks }
    })

    const inProgressStatusAndSubStatus = keys(groupedInProgressTasks)
    const inProgressData = inProgressStatusAndSubStatus.map((status) => {
      const { numberOfTasks: value, percentageOfTasks, tasks } = groupedInProgressTasks[status]
      const label = status === Status.IN_PROGRESS ? translations.inProgress : status
      const boxColor = value ? COLOR_BLACK : COLOR_GRAY
      const percentage = percentageOfTasks.toFixed(1)
      return { label, boxColor, value, percentage, tasks }
    })

    const notStartedStatusAndSubStatus = keys(groupedNotStartedTasks)
    const notStartedData = notStartedStatusAndSubStatus.map((status) => {
      const { numberOfTasks: value, percentageOfTasks, tasks } = groupedNotStartedTasks[status]
      const label = status === Status.NOT_STARTED ? translations.notStarted : status
      const boxColor = value ? COLOR_BLACK : COLOR_GRAY
      const percentage = percentageOfTasks.toFixed(1)
      return { label, boxColor, value, percentage, tasks }
    })

    const combinedData = [...completedData, ...inProgressData, ...notStartedData]
    const totalNumberOfTasks = combinedData.reduce((acc, item) => acc + item.value, 0)

    return {
      resourceName,
      totalNumberOfTasks,
      data: combinedData,
    }
  })

  return {
    shouldShowTable: tasksBasedOnResources.length > 0,
    tasksBasedOnResourcesData,
    translations,
  }
}

const ResourceParticipation = ({ projectDashboard }: ResourceParticipationProps) => {
  const { shouldShowTable, tasksBasedOnResourcesData, translations } = useResourceParticipation({ projectDashboard })
  const [expanded, setExpanded] = React.useState<string | false>(false)

  if (!shouldShowTable) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translations.noData}
      </Typography>
    )
  }

  return (
    <div>
      <Typography variant="body2" color="textSecondary">
        {tasksBasedOnResourcesData.map(({ resourceName, data, totalNumberOfTasks }) => {
          return (
            <Accordion
              key={resourceName}
              elevation={0}
              variant="outlined"
              expanded={expanded === resourceName}
              onChange={(_event, isExpanded) => {
                setExpanded(isExpanded ? resourceName : false)
              }}
            >
              <AccordionSummary expandIcon={<ChevronDown size={20} />}>
                <Grid container>
                  <Grid item xs={12} md={3}>
                    <Typography variant="body1">{resourceName}</Typography>
                    <ResourceTaskCount totalNumberOfTasks={totalNumberOfTasks} />
                  </Grid>
                  {expanded !== resourceName && (
                    <Grid item xs={12} md={9}>
                      <Grid container>
                        {data.map((item) => (
                          <Grid item key={item.label} xs={12} md={3}>
                            <Typography variant="body2" style={{ color: item.boxColor, padding: "4px" }}>
                              <span style={item.value ? { fontSize: "1.2rem" } : {}}>{item.value}</span> {item.label}
                            </Typography>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Box display="flex" flexDirection="column" width="100%">
                  {data
                    .filter((item) => item.value)
                    .map((item) => (
                      <>
                        <Typography variant="body2" key={item.label} color="textSecondary">
                          {item.label}
                        </Typography>
                        {Boolean(item.value) && (
                          <Box padding={1}>
                            {item.tasks.map((task) => (
                              <Typography key={task} variant="body2">
                                {task}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        <Box marginTop={1} />
                      </>
                    ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Typography>
    </div>
  )
}

const ResourceTaskCount = ({ totalNumberOfTasks }: { totalNumberOfTasks: number }) => {
  const translations = useTranslations({ numberOfTasks: totalNumberOfTasks })

  return (
    <Typography variant="body1" color="textSecondary">
      {translations.numberOfTasksLabel}
    </Typography>
  )
}

export default ResourceParticipation
type ResourceParticipationProps = {
  projectDashboard: ProjectDashboardViewModel
}
